import clone from 'clone';
import { defaultOffer } from '../valuesSlice';
import { isEqual, findIndex, get, isEmpty } from 'lodash-es';
import { sanitizeFloat, sanitizeKr, sanitizePercentage, sanitizeString, sanitizeInt, } from '../../../../helpers/sanitize';
import { checked } from '../../../helpers/form';
function onOfferChange(newValues, oldValues = undefined) {
    const kickback_enabled = checked(newValues.kickback_enabled);
    const items = newValues.items;
    const getItemsAttributeSum = (attributeKey, precision = 2) => {
        let result = 0;
        if (items.length) {
            result = items
                .map((item) => {
                if (get(item, 'destroy_on_save', false)) {
                    return 0;
                }
                return item[attributeKey];
            })
                .reduce((accumulator, currentValue) => accumulator + currentValue, 0);
        }
        return precision ? sanitizeFloat(result, precision) : result;
    };
    const items_price = getItemsAttributeSum('item_price');
    const items_discount = getItemsAttributeSum('item_discount');
    const items_discounted_price = getItemsAttributeSum('item_discounted_price');
    const items_tax = getItemsAttributeSum('item_tax');
    const items_taxed_price = getItemsAttributeSum('item_taxed_price');
    const items_grand_total = getItemsAttributeSum('item_total');
    return Object.assign(Object.assign({}, newValues), { items_price,
        items_discount,
        items_discounted_price,
        items_tax,
        items_taxed_price,
        items_grand_total });
}
function onOfferItemChange(newValues, oldValues = undefined) {
    let item = clone(newValues);
    const kickback_enabled = checked(item.kickback_enabled);
    const kickback_percentage = item.kickback_percentage;
    const isSubItemsItem = () => {
        const sub_items = get(item, 'sub_items', null);
        const type = get(item, 'type', null);
        return (Array.isArray(sub_items) &&
            !isEmpty(sub_items) &&
            ['grouped', 'stair'].includes(type));
    };
    const type = get(item, 'type', null);
    const steps = sanitizeInt(get(item, 'steps', 0));
    const sub_items = (function () {
        const sub_items = get(item, 'sub_items', []);
        return Array.isArray(sub_items)
            ? sub_items
                .map((sub_item) => {
                if (!isSubItemsItem()) {
                    return sub_item;
                }
                const variety = get(sub_item, 'variety', null);
                const amountFollowsNumerOfStairSteps = 'stair' === type && 'variable' === variety;
                const amountIsEditable = !amountFollowsNumerOfStairSteps;
                const amount = sanitizeString(sanitizeFloat(amountFollowsNumerOfStairSteps ? steps : sub_item.amount, 4));
                return Object.assign(Object.assign({}, sub_item), { amountIsEditable, amount });
            })
                .map(function (subItem) {
                return onOfferItemChange(subItem, null);
            })
            : [];
    })();
    const amount = sanitizeFloat(get(item, 'amount', 0), 4);
    const getSubItemsAttributeSum = (attributeKey, precision = null, amountMultiply = true) => {
        let result = 0;
        if (sub_items.length) {
            result = sub_items
                .map((subItem) => {
                if (amountMultiply) {
                    return (sanitizeFloat(subItem[attributeKey], 4) *
                        sanitizeFloat(subItem.amount, 4));
                }
                return subItem[attributeKey];
            })
                .reduce((accumulator, currentValue) => accumulator + currentValue, 0);
        }
        return precision ? sanitizeFloat(result, precision) : result;
    };
    const price = sanitizeKr(get(item, 'price', 0));
    const item_unit_price = (function () {
        const item_unit_price = isSubItemsItem()
            ? getSubItemsAttributeSum('item_unit_price_adjusted', 2, true)
            : price;
        return item_unit_price;
    })();
    const price_percentage = sanitizePercentage(item.price_percentage);
    const item_unit_price_adjusted = (function () {
        const price = isSubItemsItem()
            ? getSubItemsAttributeSum('item_unit_price_adjusted', 2, true)
            : item_unit_price;
        let price_multiplier = price_percentage / 100;
        if (price_multiplier < 0) {
            price_multiplier = 0;
        }
        return sanitizeKr(price * price_multiplier);
    })();
    const item_unit_price_kickbacked = (function () {
        if (!kickback_enabled || !kickback_percentage) {
            return item_unit_price_adjusted;
        }
        return sanitizeKr(item_unit_price_adjusted * (kickback_percentage / 100) +
            item_unit_price_adjusted);
    })();
    const item_price = (function () {
        const item_price = item_unit_price_kickbacked * amount;
        return sanitizeKr(item_price);
    })();
    const product_max_discount_percentage = (function () {
        const product_max_discount_percentage = get(item, 'product_max_discount_percentage', get(item, 'product.max_discount_percentage', 0));
        return sanitizePercentage(product_max_discount_percentage);
    })();
    const { item_max_discount_percentage, item_max_discount_amount } = (function () {
        let item_max_discount_percentage = 0;
        let item_max_discount_amount = 0;
        item_max_discount_percentage = product_max_discount_percentage;
        item_max_discount_amount = sanitizeKr((item_price / 100) * item_max_discount_percentage);
        return {
            item_max_discount_percentage,
            item_max_discount_amount,
        };
    })();
    const discount_flat = sanitizeKr(get(item, 'discount_flat', 0));
    const discount_percentage = sanitizePercentage(get(item, 'discount_percentage', 0));
    const max_discount_percentage_after_discount_flat = (function () {
        if (!item_price) {
            return 0;
        }
        return item_price
            ? ((item_max_discount_amount - discount_flat) / item_price) * 100
            : 0;
    })();
    const max_discount_percentage_after_discount_percentage = (function () {
        if (!item_price) {
            return 0;
        }
        const discount_percentage_sub_amount_calculated = (item_price / 100) * discount_percentage;
        return (((item_max_discount_amount - discount_percentage_sub_amount_calculated) /
            item_price) *
            100);
    })();
    const max_discount_flat_after_discount_percentage = sanitizeKr(amount *
        ((item_price / 100) * max_discount_percentage_after_discount_percentage));
    const discount_amount_non_validated = (function () {
        let discount_amount_non_validated = 0;
        if (discount_percentage) {
            discount_amount_non_validated = item_price * (discount_percentage / 100);
        }
        if (discount_flat) {
            discount_amount_non_validated =
                discount_amount_non_validated + discount_flat;
        }
        return sanitizeKr(discount_amount_non_validated);
    })();
    const is_discount_exceeding_max_limit = item_max_discount_amount < discount_amount_non_validated;
    const item_discount = discount_amount_non_validated < item_max_discount_amount
        ? discount_amount_non_validated
        : item_max_discount_amount;
    const item_discount_percentage = (function () {
        if (!item_price) {
            return 0;
        }
        return sanitizePercentage((item_discount / item_price) * 100);
    })();
    const item_discounted_price = (function () {
        return sanitizeKr(item_price - item_discount);
    })();
    const tax_percentage = sanitizePercentage(get(item, 'tax_percentage', 0));
    const item_tax = sanitizeKr(item_discounted_price * (tax_percentage / 100));
    const item_taxed_price = sanitizeKr(item_discounted_price + item_tax);
    const item_total = sanitizeKr(item_price - item_discount + item_tax);
    return Object.assign(Object.assign({}, item), { sub_items,
        item_unit_price,
        item_unit_price_kickbacked,
        item_unit_price_adjusted,
        item_price,
        item_max_discount_amount,
        item_max_discount_percentage,
        max_discount_percentage_after_discount_flat,
        max_discount_flat_after_discount_percentage,
        discount_amount_non_validated,
        is_discount_exceeding_max_limit,
        item_discount,
        item_discount_percentage,
        item_discounted_price,
        item_tax,
        item_taxed_price,
        item_total });
}
let prevFormState = clone(defaultOffer);
function offerFormValuesStateMachine(formsState = defaultOffer, action) {
    if (!formsState || isEqual(formsState, prevFormState)) {
        return formsState;
    }
    const prevFormStateItems = get(prevFormState, 'items', []);
    const kickback_enabled = checked(formsState.kickback_enabled);
    const kickback_percentage = formsState.kickback_percentage;
    formsState.items = formsState.items.map((item) => {
        const uid = item.uid;
        if (!uid) {
            return item;
        }
        Object.assign(item, { kickback_enabled, kickback_percentage });
        const prevFormStateIndex = findIndex(prevFormStateItems, ['uid', `${uid}`]);
        if (-1 !== prevFormStateIndex &&
            isEqual(item, prevFormStateItems[prevFormStateIndex])) {
            return item;
        }
        return onOfferItemChange(item, get(prevFormStateItems, prevFormStateIndex, null));
    });
    formsState = onOfferChange(formsState, prevFormState);
    prevFormState = clone(formsState);
    return formsState;
}
export default offerFormValuesStateMachine;
